import {ChangeDetectionStrategy, Component, EventEmitter, Input, Output} from '@angular/core';
import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {
    FieldType,
    FormCellType,
    FormField,
    FormViewModeType,
} from '@core/interfaces/engin/maintenance-planning/form-visualization';
import {Unsubscribable} from '@core/interfaces/unsubscribable';
import {filter, map, takeUntil} from 'rxjs/operators';
import {TableFieldType} from '@core/interfaces/common/form';
import {BehaviorSubject, Observable} from 'rxjs';
import {InspectionStatsDto} from '@core/interfaces/engin/maintenance-planning/deficiencies.model';

@Component({
    selector: 'ngx-form-field-base',
    template: '',
    styleUrls: ['./form-field-base.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export abstract class FormFieldBaseComponent<T> extends Unsubscribable {
    @Input() field: FormField;
    @Input() result: T;
    @Input() required: boolean;
    @Input() viewMode: FormViewModeType;
    @Input() cellType: FormCellType;
    @Input() fieldResultForm: FormGroup;
    @Input() countOfDeficiency: number = 0;
    @Input() totalDeficiencies: InspectionStatsDto;
    @Input() checkValidation: Observable<boolean> = new BehaviorSubject<boolean>(false);
    @Output() formFieldChangeEvent = new EventEmitter<{fieldType: FieldType; fieldId: number; value: T}>();
    @Output() onAddDeficiency = new EventEmitter<{fieldType: FieldType; fieldId: number}>();
    @Output() onLeadIconClick = new EventEmitter<{fieldType: FieldType; fieldId: number}>();

    FormViewModeEnum = FormViewModeType;
    FormCellType = FormCellType;

    public currentValue: T;
    public currentFieldType: T;

    protected constructor() {
        super();
    }

    protected abstract validate(value: T): boolean;

    protected abstract get fieldForm(): FormControl | FormArray;

    protected abstract applyValueChange(item: any): T;

    protected abstract getFormValue(): any;

    protected abstract onValidationCheck(): any;

    get fieldRequired(): boolean {
        return this.required && this.cellType !== FormCellType.PREVIOUS;
    }

    public emitEvent(value: T, tableField?: TableFieldType): void {
        let fieldType;
        if (tableField && value) {
            fieldType = tableField;
        } else {
            fieldType = this.field.fieldType;
        }
        this.formFieldChangeEvent.emit({fieldType: fieldType, fieldId: this.field.id, value: value});
    }

    /* Useful for simple fields which don't implement unique validation / separate form for collecting data */
    public genericInit(): void {
        // Set value based on configuration
        switch (this.viewMode) {
            case FormViewModeType.VIEW_RESULTS:
            case FormViewModeType.EDIT_RESULTS:
                this.currentValue = this.result;
                if (this.cellType != FormCellType.PREVIOUS) {
                    this.fieldForm?.setValue(this.getFormValue(), {emitEvent: false});
                }
                break;
            case FormViewModeType.VIEW_CONFIG:
                break;
        }

        // Monitor form field changes if form is in edit mode
        if (this.viewMode == FormViewModeType.EDIT_RESULTS) {
            this.fieldForm?.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((item) => {
                const newValue: T = this.applyValueChange(item);

                // Emit the new value up the component tree, to trigger further change events such as section count etc.
                this.emitEvent(newValue);

                /*
                 * Validate the field. If field is invalid but status is not INVALID, set a placeholder error
                 * 'fieldIsValid' to force status to INVALID and trigger this.fieldForm.invalid highlighting.
                 * - Simple fields should have sufficient checks for relevant errors, with associated validation messages.
                 * - Complex fields (e.g., issues, media) may need additional validation with custom logic, which can be
                 *   accessed with this custom error code 'fieldIsValid'.
                 * Note: .setErrors({fieldIsValid: true}) still sets form status to invalid! Instead .setErrors(null)
                 */
                const isValid = this.validate(newValue);
                if (!isValid && this.fieldForm.valid) {
                    this.fieldForm.setErrors({fieldIsValid: false});
                }
            });
        }

        // This triggers from top-down request for form validation; re-validate the field
        this.checkValidation
            .pipe(
                takeUntil(this.unsubscribe$),
                filter((value) => !!value),
                map((_) => {
                    this.onValidationCheck();
                }),
            )
            .subscribe();
    }

    /**
     * Finds the count of deficiencies in the current section if present
     * @param formField FormField to find the count of deficiencies for
     * @returns number of deficiencies for a given section field
     */
    findInspectionDeficiencyCount(formField: FormField): number {
        if (this.totalDeficiencies?.stats) {
            return this.totalDeficiencies.stats
                .filter(
                    (s) =>
                        s.formSectionId === formField.sectionId && // filter by parent section
                        s.formFieldId === formField.id,
                ) // and by form field id
                .reduce((a, b) => a + b.countOfDeficiency, 0);
        } else {
            return 0;
        }
    }

    /**
     * Emits event when the add deficiency button is clicked for a specific field of the form.
     * @param {{ fieldType: FieldType, fieldId: number }} field
     *      The field object containing the field type and id which the add deficiency button was clicked for.
     */
    onAddDeficiencyClicked() {
        this.onAddDeficiency.emit({fieldType: this.field.fieldType, fieldId: this.field.id});
    }
}
